import { useMutation, useQueryClient } from '@tanstack/react-query'

const mutateAdCampaignDelivery = (ad) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['ad-campaign-delivery', ad],
        mutationFn: async (formData) => {
            const res = await axios.put(route('studio.v1.api.campaign.delivery.upsert', ad), formData);
            return res;
        },
        // The response is already the saved campaign in the shape the query caches, so it
        // is seeded rather than invalidated: a refetch re-rendered this step against
        // half-loaded data before the navigation landed, dropping the picked package.
        onSuccess: (res) => {
            if (res?.data?.data) {
                // This ad's queries only: the key's prefix alone matches every ad.
                queryClient.setQueriesData({ queryKey: ['api-v1-studio-get-campaign', ad] }, res.data);
            }
        },
    });
}

const mutateAdCampaignTargeting = (ad) => {

    return useMutation({
        mutationKey: ['ad-campaign-targeting', ad],
        mutationFn: async (formData) => {
            const res = await axios.put(route('studio.v1.api.campaign.targeting.upsert', ad), formData);
            return res;
        },
    });
}

const mutateAdCampaignTargetingSearch = () => {
    return useMutation({
        mutationKey: ['ad-campaign-targeting-search'],
        mutationFn: async (params) => {
            const res = await axios.get(route('studio.v1.api.campaign.targeting.search'), {
                params
            });
            return res?.data?.data ?? [];
        },
    });
}

const mutateAdCampaignLaunch = (ad) => {

    return useMutation({
        mutationKey: ['ad-campaign-launch', ad],
        mutationFn: async (formData) => {
            const res = await axios.put(route('studio.v1.api.campaign.launch', ad), formData);
            return res;
        },
    });
}

const mutateAdCampaignLaunchManual = (ad) => {

    return useMutation({
        mutationKey: ['ad-campaign-launch-manual', ad],
        mutationFn: async (formData) => {
            const res = await axios.put(route('studio.v1.api.campaign.launch-manual', ad), formData);
            return res;
        },
    });
}

const useMutateAdCreativeHtml = (adId, adBannerFormatId) => {
    return useMutation({
        mutationKey: ['ad-campaign-creative-html', adId, adBannerFormatId],

        mutationFn: async (file) => {
            const formData = new FormData();

            formData.append('zip', file);

            const { data } = await axios.post(
                route('studio.v1.api.ads.html5-creative.store',[adId, adBannerFormatId]),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return data;
        },
    });
};

const regionGeometryParams = (label, targetingOptionId, geoRegionType) => ({
    name: (label ?? '').trim(),
    id: targetingOptionId ?? undefined,
    type: geoRegionType ?? undefined,
});

const fetchRegionGeometry = async ({ name, id, type }) => {
    if (!name) return null;

    const res = await axios.get(route('studio.v1.api.campaign.targeting.region-bounds'), {
        params: { name, id, type },
    });

    return res?.data?.data ?? null;
}

const mutateGetRegionGeometry = () => {
    return useMutation({
        mutationKey: ['ad-region-geometry'],
        mutationFn: fetchRegionGeometry,
        retry: 3,
    });
}


/**
 * AD-187: Profile Settings — the caller's own name, e-mail and language. The saved
 * profile comes back in the shape the profile query caches, so it is seeded directly.
 */
// The DataTableUi cache of the User Management table (tableKey "users-index").
const USERS_TABLE_KEY = ['ssr-datatable-users-index'];

const mutateProfileUpdate = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['profile-update'],
        mutationFn: (formData) => axios.put(route('studio.v1.api.profile.update'), formData),
        onSuccess: (res) => {
            if (res?.data) {
                queryClient.setQueryData(['api-v1-studio-profile'], res.data);
            }
        },
    });
}

const mutateProfileContact = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['profile-contact'],
        mutationFn: (formData) => axios.put(route('studio.v1.api.profile.contact.update'), formData),
        onSuccess: (res) => {
            if (res?.data) {
                queryClient.setQueryData(['api-v1-studio-profile'], res.data);
            }
        },
    });
}

/**
 * AD-187: Profile Settings — change the caller's own password.
 */
const mutateProfilePassword = () => {

    return useMutation({
        mutationKey: ['profile-password'],
        mutationFn: (formData) => axios.put(route('studio.v1.api.profile.password.update'), formData),
    });
}

/**
 * AD-187: invite a colleague. The User Management table is refetched so the new
 * Pending row shows straight away.
 */
const mutateInviteUser = () => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['user-invite'],
        mutationFn: (formData) => axios.post(route('studio.v1.api.users.invite'), formData),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_TABLE_KEY }),
    });
}

/**
 * AD-187: an admin editing somebody else's account.
 */
const mutateUserUpdate = (user) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['user-update', user],
        mutationFn: (formData) => axios.put(route('studio.v1.api.users.update', user), formData),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_TABLE_KEY }),
    });
}

/**
 * AD-187: deactivate or reactivate somebody else's account.
 */
const mutateUserStatus = (user) => {

    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['user-status', user],
        mutationFn: (status) => axios.put(route('studio.v1.api.users.status.update', user), { status }),
        // Not awaited: the refetch swaps the rows for skeletons, which unmounts the
        // row's actions — the caller's success toast would never run.
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: USERS_TABLE_KEY });
        },
    });
}

/**
 * AD-187: the invitee finishing their account from the invitation link.
 */
const mutateAcceptInvitation = (token) => {

    return useMutation({
        mutationKey: ['invitation-accept', token],
        mutationFn: (formData) => axios.post(route('studio.v1.api.invitation.accept', token), formData),
    });
}

export {
    mutateProfileUpdate,
    mutateProfileContact,
    mutateProfilePassword,
    mutateInviteUser,
    mutateUserUpdate,
    mutateUserStatus,
    mutateAcceptInvitation,
    mutateAdCampaignDelivery,
    mutateAdCampaignTargeting,
    mutateAdCampaignTargetingSearch,
    mutateAdCampaignLaunch,
    mutateAdCampaignLaunchManual,
    mutateGetRegionGeometry,
    fetchRegionGeometry,
    regionGeometryParams,
    useMutateAdCreativeHtml
}